import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import Tabs, { TabList, TabPanel, useTab } from '@atlaskit/tabs';
import { Box, Stack, Inline, Text, Pressable } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';

import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_INSIGHTS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';

import { useAudioStream } from '../../hooks/useAudioStream';
import { useAudioEventListener } from '../../hooks/useAudioEventListener';
import { useAudioBriefing } from '../../hooks/useAudioBriefing';
import { AudioTimeIndicator } from '../controls/AudioTimeIndicator';
import type { CustomLengthType, CustomToneType } from '../../types/customizationTypes';
import { CustomLength, CustomTone } from '../../types/customizationTypes';

import { AudioBriefingChangeboarding } from './AudioBriefingChangeboarding';
import { AudioBriefingPopupControlButton } from './AudioBriefingPopupControlButton';
import { AudioBriefingPopupListenPanel } from './panels/listen/AudioBriefingPopupListenPanel';
import { AudioBriefingPopupCustomizePanel } from './panels/customize/AudioBriefingPopupCustomizePanel';
import { AudioBriefingPopupSourcesPanel } from './panels/sources/AudioBriefingPopupSourcesPanel';
import { AudioBriefingPopupFeedbackFooter } from './AudioBriefingPopupFeedbackFooter';
import { AudioBriefingErrorComponent } from './AudioBriefingErrorComponent';

const i18n = defineMessages({
	listenTab: {
		id: 'audio.audio-briefing-popup.listen-tab',
		defaultMessage: 'Listen',
		description:
			'The name of the tab in the audio briefing popup that directs towards the miniplayer to listen to audio',
	},
	customizeTab: {
		id: 'audio.audio-briefing-popup.customize-tab',
		defaultMessage: 'Customize',
		description:
			'The name of the tab in the audio briefing popup that directs towards the customization panel',
	},
	sourcesTab: {
		id: 'audio.audio-briefing-popup.sources-tab',
		defaultMessage: 'Sources',
		description:
			'The name of the tab in the audio briefing popup that directs towards the sources panel',
	},
	briefingTitle: {
		id: 'audio.audio-briefing-popup.briefing-title',
		defaultMessage: 'Briefing: {contentTitle}',
		description: 'Title of the audio briefing popup which takes in the actual page title',
	},
	briefingBylineTemplate: {
		id: 'audio.audio-briefing-popup.briefing-byline-template',
		defaultMessage: '{length} briefing • {tone} tone',
		description: 'Template for the byline that displays length and tone of the briefing',
	},
	lengthShort: {
		id: 'audio.audio-briefing-popup.length-short',
		defaultMessage: 'concise',
		description: 'Label for short (concise) briefing length option',
	},
	lengthMedium: {
		id: 'audio.audio-briefing-popup.length-medium',
		defaultMessage: 'medium length',
		description: 'Label for medium length briefing option',
	},
	lengthLong: {
		id: 'audio.audio-briefing-popup.length-long',
		defaultMessage: 'extended',
		description: 'Label for long (extended) briefing length option',
	},
	toneProfessional: {
		id: 'audio.audio-briefing-popup.tone-professional',
		defaultMessage: 'professional',
		description: 'Label for professional tone option',
	},
	toneConversational: {
		id: 'audio.audio-briefing-popup.tone-conversational',
		defaultMessage: 'conversational',
		description: 'Label for conversational tone option',
	},
	tonePlayful: {
		id: 'audio.audio-briefing-popup.tone-playful',
		defaultMessage: 'playful',
		description: 'Label for playful tone option',
	},
	toneSurprise: {
		id: 'audio.audio-briefing-popup.tone-surprise',
		defaultMessage: 'surprise me',
		description: 'Label for surprise tone option',
	},
	closeButtonLabel: {
		id: 'audio.audio-briefing-popup.close-label',
		defaultMessage: 'Close',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will close the audio briefings popup',
	},
});

type AudioBriefingPopupContentProps = {
	closePopup: () => void;
};

type AudioBriefingPopupContentComponentProps = {
	closePopup: () => void;
	audioRef: React.RefObject<HTMLAudioElement>;
};

type MinimizedPlayerProps = {
	expandPopup: () => void;
	audioRef: React.RefObject<HTMLAudioElement>;
	isPlaying: boolean;
	isLoading: boolean;
};

const tabsListStyles = css({
	marginBottom: token('space.150'),
	marginTop: token('space.150'),
	padding: token('space.050'),
	border: `1px solid ${token('color.border')}`,
	borderRadius: token('border.radius.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div[role=tablist]': {
		marginInlineStart: 'unset',
		gap: token('space.050'),
		display: 'flex',
		width: '100%',
		'&::before': {
			all: 'unset',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[role=tab]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&[aria-selected=true]::after': {
				insetInlineStart: 'unset',
			},
			'&:hover::after': {
				insetInlineStart: 'unset',
			},
		},
	},
});

const styles = cssMap({
	wrapper: {
		width: '300px',
	},
	footer: {
		backgroundColor: token('elevation.surface.sunken'),
	},
	headerTitle: {
		flex: 1,
	},
	minimizedWrapper: {
		width: '105px',
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
	},
	customTab: {
		paddingBlock: token('space.025'),
		borderRadius: token('border.radius'),
		flex: 1,
		backgroundColor: token('elevation.surface'),
		fontWeight: token('font.weight.medium'),
		font: token('font.body.small'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
	selectedTab: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
	},
});

const CustomTab = ({ label }: { label: string }) => {
	const tabAttributes = useTab();
	return (
		<Pressable
			{...tabAttributes}
			xcss={cx(styles.customTab, tabAttributes['aria-selected'] && styles.selectedTab)}
		>
			{label}
		</Pressable>
	);
};

const MinimizedPlayer = ({
	expandPopup,
	audioRef,
	isLoading,
}: MinimizedPlayerProps): JSX.Element => {
	return (
		<Pressable
			xcss={styles.minimizedWrapper}
			onClick={expandPopup}
			testId="briefings-minimized-player"
		>
			<Box padding="space.050">
				<AudioTimeIndicator audioRef={audioRef} isLoading={isLoading} variant="blue" />
			</Box>
		</Pressable>
	);
};

const getLengthMessage = (length: CustomLengthType) => {
	switch (length) {
		case CustomLength.SHORT:
			return i18n.lengthShort;
		case CustomLength.MEDIUM:
			return i18n.lengthMedium;
		default:
			return i18n.lengthLong;
	}
};

const getToneMessage = (tone: CustomToneType) => {
	switch (tone) {
		case CustomTone.CONVERSATIONAL:
			return i18n.toneConversational;
		case CustomTone.PLAYFUL:
			return i18n.tonePlayful;
		case CustomTone.SURPRISE:
			return i18n.toneSurprise;
		default:
			return i18n.toneProfessional;
	}
};

const AudioBriefingPopupContentComponent = ({
	closePopup,
	audioRef,
}: AudioBriefingPopupContentComponentProps) => {
	const { formatMessage } = useIntl();

	const [{ activeContent, isPlaying, error, playbackSpeed, isLoading, customization }] =
		useAudioBriefing();

	useEffect(() => {
		const audio = audioRef.current;
		if (audio && playbackSpeed && fg('confluence_briefings_playback_speed')) {
			audio.playbackRate = playbackSpeed;
		}
	}, [playbackSpeed, audioRef, isLoading]);

	const [isMinimized, setIsMinimized] = useState(false);

	const contentTitle = activeContent?.contentTitle;

	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		ONBOARDING_INSIGHTS.AUDIO_BRIEFING,
	);

	const { hasAudioBriefingChangeboardingBeenSeen } = deserializeState(onboardingStateData);

	if (error) {
		return (
			<AudioBriefingErrorComponent error={error} audioRef={audioRef} closePopup={closePopup} />
		);
	}

	if (onboardingStateLoading) {
		return null;
	}

	if (!hasAudioBriefingChangeboardingBeenSeen) {
		return <AudioBriefingChangeboarding />;
	}

	const lengthMessage = getLengthMessage(customization.length);
	const toneMessage = getToneMessage(customization.tone);
	const translatedLength = formatMessage(lengthMessage);
	const translatedTone = formatMessage(toneMessage);

	return (
		<>
			{isMinimized ? (
				<MinimizedPlayer
					expandPopup={() => {
						setIsMinimized(false);
					}}
					audioRef={audioRef}
					isPlaying={isPlaying}
					isLoading={isLoading}
				/>
			) : (
				<Box xcss={styles.wrapper}>
					<Stack>
						<Box padding="space.200">
							<Inline grow="fill" space="space.150">
								<Stack xcss={styles.headerTitle}>
									<Heading size="xsmall">
										{formatMessage(i18n.briefingTitle, { contentTitle })}
									</Heading>
									<Text color="color.text.subtlest" size="small" maxLines={1}>
										{formatMessage(i18n.briefingBylineTemplate, {
											length: translatedLength,
											tone: translatedTone,
										})}
									</Text>
								</Stack>

								{/* Renders a close button if the audio is not playing,
								otherwise renders a minimize button */}
								<AudioBriefingPopupControlButton
									isPlaying={isPlaying}
									minimizePopup={() => {
										setIsMinimized(true);
									}}
									closePopup={closePopup}
								/>
							</Inline>

							<Tabs id="audio-tabs">
								<div css={tabsListStyles}>
									<TabList>
										<CustomTab label={formatMessage(i18n.listenTab)} />
										<CustomTab label={formatMessage(i18n.customizeTab)} />
										{fg('confluence_briefings_sources_tab') && (
											<CustomTab label={formatMessage(i18n.sourcesTab)} />
										)}
									</TabList>
								</div>
								<TabPanel>
									<AudioBriefingPopupListenPanel audioRef={audioRef} />
								</TabPanel>
								<TabPanel>
									<AudioBriefingPopupCustomizePanel audioRef={audioRef} />
								</TabPanel>
								{fg('confluence_briefings_sources_tab') && (
									<TabPanel>
										<AudioBriefingPopupSourcesPanel />
									</TabPanel>
								)}
							</Tabs>
						</Box>
						<Box xcss={styles.footer} paddingBlock="space.100" paddingInline="space.200">
							<AudioBriefingPopupFeedbackFooter activeContent={activeContent} />
						</Box>
					</Stack>
				</Box>
			)}
		</>
	);
};

export const AudioBriefingPopupContentAudio = ({
	closePopup,
}: AudioBriefingPopupContentProps): JSX.Element => {
	const [{ stream }, { pauseContent, resumeContent, endContent, setContentError }] =
		useAudioBriefing();
	const { audioRef } = useAudioStream(stream, setContentError);
	useAudioEventListener({
		audioRef,
		onContentPaused: pauseContent,
		onContentResumed: resumeContent,
		onContentEnded: endContent,
	});

	return (
		<>
			{/* eslint-disable-next-line jsx-a11y/media-has-caption -- The summary is already available in text so the track isn't very necessary */}
			<audio data-testid="briefings-audio" ref={audioRef} />
			<AudioBriefingPopupContentComponent closePopup={closePopup} audioRef={audioRef} />
		</>
	);
};

export const AudioBriefingPopupContent = ({
	closePopup,
}: AudioBriefingPopupContentProps): JSX.Element => {
	return (
		<ErrorBoundary attribution={Attribution.SMARTS}>
			<AudioBriefingPopupContentAudio closePopup={closePopup} />
		</ErrorBoundary>
	);
};
